
import InspectionTime from "./InspectionTime";
import ExternalLink from "./ExternalLink";
import { EmbeddedContent } from "./EmbeddedContent/EmbeddedContent";
import { Profile } from "./Profile";
import { ResizableImage } from "./ResizableImage";
import Coordinate from "./Coordinate";

interface ListingAddress {
    DisplayAddress: boolean;

    UnitNumber: string;
    StreetNumber: string;
    Address: string;
    Suburb: string;
    PostCode: string;
    Estate: string;
    State: string;

    FullAddress: string;
    MicroAddress: string;
    HiddenAddress: string;

    Coordinates: Coordinate;
}

interface ListingPrice {
    Price: string;
    DisplayPrice: boolean;
    BondPrice: string;
    NumericPrice: number;
}

interface ListingParking {
    Garages: number;
    CarPorts: number;
    OpenSpaces: number;
    CarSpaces: number;
    Total: number;
}

enum SupplementaryType {
    None,
    Pool,
    Study
}

interface Supplementary {
    Type: SupplementaryType;
    Count: Number;
}

interface ListingArea {
    Office: string;
    Warehouse: string;
    TotalBuilding: string;
    Land: string;

    Other: string;
    OtherType: string;

    Frontage: string;
    DepthLeft: string;
    DepthRight: string;
    DepthBack: string;
    
    Crossover: string;
}

interface ListingFeatures {
    Bedrooms: number;
    Bathrooms: number;

    Parking: ListingParking;

    Supplementary1: Supplementary;
    Supplementary2: Supplementary;

    Area: ListingArea;

    NumberOfLevels: number;

    OtherFeatures: string;

    HasGreyWaterSystem: boolean;
    HasSolarHotWater: boolean;
    HasSolarPanels: boolean;
    HasWaterTank: boolean;
    HasAirConditioning: boolean;
    HasDuctedCooling: boolean;
    HasDuctedHeating: boolean;
    HasEvaporativeCooling: boolean;
    HasGasHeating: boolean;
    HasHydronicHeating: boolean;
    HasOpenFirePlace: boolean;
    HasReverseCycleAirCon: boolean;
    HasSplitSystemAirCon: boolean;
    HasSplitSystemHeating: boolean;
    HasAlarmSystem: boolean;
    HasBroadband: boolean;
    HasBuiltInRobes: boolean;
    HasDishwasher: boolean;
    HasVacuumSystem: boolean;
    HasFloorboards: boolean;
    HasGym: boolean;
    HasInsideSpa: boolean;
    HasIntercom: boolean;
    HasPayTV: boolean;
    HasRumpusRoom: boolean;
    HasStudy: boolean;
    HasWorkshop: boolean;
    HasBalcony: boolean;
    HasCourtyard: boolean;
    HasDeck: boolean;
    HasFullyFenced: boolean;
    HasOutsideSpa: boolean;
    HasRemoteGarage: boolean;
    HasSecureParking: boolean;
    HasShed: boolean;
    HasPoolAboveGround: boolean;
    HasPoolInGround: boolean;
    HasTennisCourt: boolean;
    HasOutDoorEntertainment: boolean;

    IsPetFriendly: boolean;
    IsFurnished: boolean;

    FeatureList: string[];
}

enum ListingFileType {
    None,
    PDF,
    Floorplan
}

interface ListingFile {
    Type: ListingFileType;
    Label: string;
    Url: string;
}

interface Listing {
    Id: string;
    PropertyId: number;
    CreateDate: Date;

    ListingCategory: string;
    PropertyType: string;
    MethodOfSale: string;
    Status: string;

    CommercialCategories: string[];
    Agents: Profile[];

    Address: ListingAddress;
    Price: ListingPrice;
    Features: ListingFeatures;

    InspectionTimes: InspectionTime[];

    WholeOrPart: string;
    Tenancy: string;

    WaterRates: string;
    CouncilRates: string;

    Header: string;
    Introduction: string;
    WebDescription: string;
 
    HtmlWebDescription: string;

    ExternalLinks: ExternalLink[];
    EmbeddedContent: EmbeddedContent[];
    Files: ListingFile[];

    Images: ResizableImage[];
    DateAvailable: Date;
    
    IsHighlightListing: boolean;

    BulletPoints: string[];

    Slug: string;
}

enum ListingCategory
{
    ResidentialSale,
    ResidentialRental,
    ResidentialLand,
    HolidayRental,
    Rural,
    Commercial,
    CommercialLand,
    Business
}

enum ListingStatus
{
    Current,
    UnderContract,
    OffMarket,
    Withdrawn,
    Leased,
    Sold
}

enum MethodOfSale
{ 
    Sale,
    Lease,
    Both,
    Auction,
    Tender,
    EOI,
    ForSale,
    Offers
}

enum PropertyType
{
    Acreage,
    Alpine,
    Apartment,
    BlockOfUnits,
    Duplex,
    Flat,
    House,
    Retirement,
    SemiDetached,
    ServicedApartment,
    Studio,
    Terrace,
    Townhouse,
    Unit,
    Villa,
    Warehouse,
    // Rural
    Cropping,
    Dairy,
    Farmlet,
    Horticulture,
    Lifestyle,
    Livestock,
    MixedFarming,
    Viticulture,
    Other
}

enum PropertyCategory
{
    AccommodationTourism,
    Automotive,
    BeautyHealth,
    CommercialFarming,
    FoodHospitality,
    HomeGarden,
    HotelLeisure,
    IndustrialManufacturing,
    IndustrialWarehouse,
    LandDevelopment,
    LeisureEntertainment,
    MedicalConsulting,
    Offices,
    Other,
    Professional,
    Retail,
    Rural,
    Services,
    ShowroomsBulkyGoods,
    TransportDistribution
}

enum PropertySubCategory
{
    AccessoriesParts,
    AgedCare,
    Agricultural,
    AlcoholLiquor,
    Amusements,
    Aquaculture,
    AquaticMarine,
    BackpackerHostel,
    Bakery,
    BarsNightclubs,
    BeautySalon,
    BoatsMarine,
    BuildingandConstruction,
    Bus,
    Butcher,
    CafeCoffeeShop,
    CarBusTruck,
    Catering,
    Cleaning,
    ClothingAccessories,
    ConvenienceStore,
    DairyFarming,
    Dental,
    Farming,
    FishingForestry,
    FloristNursery,
    FoodBeverage,
    FruitVeg,
    FunctionCentre,
    FurnitureTimber,
    GardenHousehold,
    GardenNurseries,
    GlassCeramic,
    Hair,
    HealthSpa,
    HireRent,
    HomewareHardware,
    Hotel,
    JuiceBar,
    LimousineTaxi,
    Machinery,
    MachineryMetal,
    ManagementRights,
    Manufacturers,
    MechanicalRepair,
    Media,
    Motel,
    MusicVideo,
    Nursery,
    Other,
    PrintPhoto,
    RecreationSport,
    Repair,
    Resort,
    Restaurant,
    ServiceStation,
    SportsComplexGym,
    TakeawayFood,
    Tours,
    Wholesalers
}

export { ListingAddress, ListingPrice, ListingParking, SupplementaryType, Supplementary, ListingArea, ListingFeatures, ListingFileType, ListingFile, Listing, ListingCategory, ListingStatus, MethodOfSale, PropertyType, PropertyCategory, PropertySubCategory }
